/** Homepage motion: native sticky layout, one frame per scroll, static HTML first. */
(() => {
  const clamp = (value) => Math.max(0, Math.min(1, value));
  // Shared vocabulary: ramp a value over a slice of progress, and travel the
  // same distance whenever something enters or leaves.
  const ramp = (progress, start, span) => clamp((progress - start) / span);
  const shift = (amount) => `translateY(${amount * 12}px)`;

  // Only geometry/lifecycle is shared; each scene owns its visual behavior.
  function observeScroll(root, render, canAnimate = () => true) {
    const frame = root.querySelector('[data-scroll-frame]');
    const content = root.querySelector('[data-scroll-content]');
    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    const controller = new AbortController();
    let pending = 0;
    let disposed = false;
    const update = () => {
      pending = 0;
      if (disposed) return;
      const styles = getComputedStyle(frame);
      const viewport = root.querySelector('[data-viewport-measure]').offsetHeight;
      const gap = parseFloat(styles.rowGap) || 0;
      const contentShare = parseFloat(styles.getPropertyValue('--scroll-content-share')) || 1;
      const contentHeight = content.offsetHeight;
      const occupied = contentHeight + gap + parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
      const fits = occupied <= viewport && contentHeight <= viewport * contentShare;
      const active = !motion.matches && !window.Shopify?.designMode && fits && canAnimate();
      // toggleAttribute is a no-op when the attribute already matches `active`.
      root.toggleAttribute('data-motion', active);
      const bounds = root.getBoundingClientRect();
      const travel = bounds.height - frame.offsetHeight;
      const progress = active && travel > 0 ? clamp(-bounds.top / travel) : 1;
      render(progress, active);
    };
    const schedule = () => { if (!pending && !disposed) pending = requestAnimationFrame(update); };
    for (const name of ['scroll', 'resize', 'pageshow']) {
      window.addEventListener(name, schedule, { passive: true, signal: controller.signal });
    }
    motion.addEventListener('change', schedule, { signal: controller.signal });
    const observer = new ResizeObserver(schedule);
    observer.observe(content);
    observer.observe(frame);
    update();
    return {
      refresh: schedule,
      dispose() {
        disposed = true;
        controller.abort();
        observer.disconnect();
        cancelAnimationFrame(pending);
        root.removeAttribute('data-motion');
      },
    };
  }

  class ScrollVideoHero extends HTMLElement {
    connectedCallback() {
      if (this.lifecycle) return;
      this.lifecycle = new AbortController();
      const { signal } = this.lifecycle;
      const video = this.querySelector('video');
      const secondLine = this.querySelector('[data-hero-final]');
      const actions = this.querySelector('[data-hero-actions]');
      const reduced = matchMedia('(prefers-reduced-motion: reduce)');
      // Keep progress on the element so resize/reconnection cannot replay it.
      // A new document creates a new element and starts a fresh presentation.
      this.playbackProgress ??= 0;
      let ready = false;
      let failed = false;
      let target = 0;
      let loadTimer;
      const seek = () => {
        if (!ready || video.seeking || Math.abs(video.currentTime - target) < 1 / 30) return;
        try { video.currentTime = target; } catch { fail(); }
      };
      const fail = () => {
        failed = true;
        ready = false;
        clearTimeout(loadTimer);
        this.removeAttribute('data-decoded');
        this.scene?.refresh();
      };
      const loaded = () => {
        if (!Number.isFinite(video.duration) || video.duration <= 0) return fail();
        ready = true;
        clearTimeout(loadTimer);
        this.setAttribute('data-decoded', '');
        this.scene?.refresh();
        tryIntro();
      };
      // Visitors who have not scrolled 0.7s after arriving get the scene
      // played for them, since the copy and buttons are tied to scroll. Any
      // input, or a scroll the intro did not make, hands control back.
      const intro = new AbortController();
      let introDue = false;
      let introFrame = 0;
      let introY = 0;
      const stopIntro = () => {
        introDue = false;
        clearTimeout(introTimer);
        cancelAnimationFrame(introFrame);
        intro.abort();
      };
      const tryIntro = () => {
        if (!introDue || !ready || intro.signal.aborted) return;
        if (!this.hasAttribute('data-motion') || document.visibilityState !== 'visible') return;
        introDue = false;
        const frame = this.querySelector('[data-scroll-frame]');
        const start = scrollY;
        const end = this.getBoundingClientRect().top + scrollY + this.offsetHeight - frame.offsetHeight;
        // Plays 30% faster than the video's own length.
        const duration = Math.min(4000, Math.max(1500, video.duration * 1000)) / 1.3;
        const began = performance.now();
        const step = (now) => {
          if (!this.hasAttribute('data-motion')) return stopIntro();
          const t = Math.min(1, (now - began) / duration);
          const eased = t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
          introY = start + (end - start) * eased;
          scrollTo(0, introY);
          if (t < 1) introFrame = requestAnimationFrame(step);
          else stopIntro();
        };
        introFrame = requestAnimationFrame(step);
      };
      const introTimer = setTimeout(() => {
        introDue = true;
        tryIntro();
      }, 700);
      // Tests that drive scroll themselves opt out with MOSES_HERO_INTRO.
      if (scrollY > 1 || this.playbackProgress > 0 || window.MOSES_HERO_INTRO === false) stopIntro();
      for (const name of ['wheel', 'touchstart', 'pointerdown', 'keydown']) {
        addEventListener(name, stopIntro, { passive: true, signal: intro.signal });
      }
      addEventListener('scroll', () => {
        if (Math.abs(scrollY - introY) > 2) stopIntro();
      }, { passive: true, signal: intro.signal });
      document.addEventListener('visibilitychange', tryIntro, { signal: intro.signal });
      this.scene = observeScroll(this, (progress, active) => {
        if (active) {
          this.playbackProgress = Math.max(this.playbackProgress, progress);
          progress = this.playbackProgress;
        }
        if (secondLine) secondLine.style.opacity = active ? String(ramp(progress, 0.35, 0.2)) : '1';
        // Keep geometry stable, but prevent invisible controls from receiving focus.
        if (actions) {
          const reveal = active ? ramp(progress, 0.65, 0.2) : 1;
          actions.style.opacity = String(reveal);
          actions.style.visibility = reveal === 0 ? 'hidden' : 'visible';
          actions.inert = reveal < 1;
        }
        if (!active || !ready) return;
        target = progress * Math.max(0, video.duration - 1 / 30);
        seek();
      }, () => Boolean(video) && !failed);
      if (video) {
        video.pause();
        video.addEventListener('loadeddata', loaded, { signal });
        video.addEventListener('seeked', seek, { signal });
        video.addEventListener('error', fail, { signal, capture: true });
        const load = () => {
          if (reduced.matches || window.Shopify?.designMode || failed) return;
          if (video.readyState >= 2) return loaded();
          if (video.preload !== 'auto') {
            video.preload = 'auto';
            video.load();
          }
          clearTimeout(loadTimer);
          loadTimer = setTimeout(fail, 15000);
        };
        reduced.addEventListener('change', load, { signal });
        load();
      }
      this.cleanupMedia = () => { clearTimeout(loadTimer); stopIntro(); video?.pause(); };
    }
    disconnectedCallback() {
      this.lifecycle?.abort();
      this.cleanupMedia?.();
      this.scene?.dispose();
      this.lifecycle = null;
      this.scene = null;
    }
  }

  class ScrollWordStory extends HTMLElement {
    connectedCallback() {
      if (this.scene || this.hasAttribute('data-static')) return;
      const words = [...this.querySelectorAll('[data-story-word]')];
      const content = this.querySelector('[data-scroll-content]');
      const actions = this.querySelector('[data-story-actions]');
      this.scene = observeScroll(this, (progress, active) => {
        // Stagger only the entrance; the common parent owns the entire exit.
        const leave = active ? ramp(progress, 0.65, 0.3) : 0;
        content.style.opacity = String(1 - leave);
        content.style.transform = active ? shift(-leave) : 'none';
        words.forEach((word, index) => {
          const enter = active ? ramp(progress, index * 0.3 / words.length, 0.3 / words.length) : 1;
          word.style.opacity = String(enter);
          word.style.transform = active ? shift(1 - enter) : 'none';
        });
        if (actions) {
          // Both links appear after the final word, then leave with the heading.
          const enter = active ? ramp(progress, 0.32, 0.15) : 1;
          actions.style.opacity = String(enter);
          actions.style.transform = active ? shift(1 - enter) : 'none';
          actions.style.visibility = enter === 0 || leave === 1 ? 'hidden' : 'visible';
          actions.inert = enter < 1 || leave > 0;
        }
      }, () => words.length > 0);
    }
    disconnectedCallback() {
      this.scene?.dispose();
      this.scene = null;
    }
  }
  if (!customElements.get('scroll-word-story')) customElements.define('scroll-word-story', ScrollWordStory);
  if (!customElements.get('scroll-video-hero')) customElements.define('scroll-video-hero', ScrollVideoHero);
})();
